import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

import type { ToolConfig } from "types";
import useOutsideClick from "hooks/useClickAway";
import UploadButton from "components/UploadButton";
import styles from "./PlusMenu.module.scss";

interface ToolMenuItemProps {
  tool: ToolConfig;
  onToggle: (key: string) => void;
}

function ToolMenuItem({ tool, onToggle }: ToolMenuItemProps) {
  const button = (
    <button
      type="button"
      className={`${styles.menuItem} ${tool.enabled ? styles.active : ""} ${
        tool.disabled ? styles.menuItemDisabled : ""
      }`}
      onClick={() => {
        if (!tool.disabled) onToggle(tool.key);
      }}
      disabled={tool.disabled}
    >
      <FontAwesomeIcon icon={tool.icon} className={styles.menuItemIcon} />
      {tool.label}
      <span className={styles.toggleIndicator}>
        {tool.enabled ? "On" : "Off"}
      </span>
    </button>
  );

  if (tool.disabled && tool.disabledReason) {
    return (
      <Tippy content={tool.disabledReason} placement="right">
        <span>{button}</span>
      </Tippy>
    );
  }

  return button;
}

interface PlusMenuProps {
  tools: ToolConfig[];
  onToggleTool: (key: string) => void;
  onFileDrop?: (files: File[]) => void;
  uploadDisabled?: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function PlusMenu({
  tools,
  onToggleTool,
  onFileDrop,
  uploadDisabled = false,
  isOpen,
  setIsOpen,
}: PlusMenuProps) {
  // Stable reference prevents useOutsideClick from re-registering
  // the document listener on every render.
  const handleOutsideClick = React.useCallback(() => {
    if (isOpen) setIsOpen(false);
  }, [isOpen, setIsOpen]);

  const menuRef = useOutsideClick(handleOutsideClick);

  const handleFileSelect = (files: File[]) => {
    if (onFileDrop) {
      onFileDrop(files);
    }
    setIsOpen(false);
  };

  return (
    <div className={styles.container} ref={menuRef}>
      <Tippy content="Upload files and more" placement="top">
        <button
          type="button"
          className={styles.plusButton}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Upload files and more"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </Tippy>

      {isOpen && (
        <div className={styles.popover}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>Files</div>
            <UploadButton
              onFileSelect={handleFileSelect}
              disabled={uploadDisabled}
            />
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <div className={styles.sectionHeader}>Tools</div>
            {tools.map((tool) => (
              <ToolMenuItem
                key={tool.key}
                tool={tool}
                onToggle={onToggleTool}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
